import {
  ComponentRef,
  TemplateRef,
  ReflectiveInjector,
  ResolvedReflectiveProvider, Type
} from '@angular/core';

import { Overlay } from '../overlay/index';
import { Class, Maybe } from '../framework/utils';
import { OverlayConfig, ContainerContent } from '../models/tokens';
import { DialogRef } from '../models/dialog-ref';
import { ModalControllingContextBuilder } from '../models/overlay-context';
export class UnsupportedDropInError extends Error {
/**
 * @param {?} dropInName
 */
constructor(dropInName: string) {
    super();
    this.message = `Unsupported Drop-In ${dropInName}`;
  }
}
/**
 * @abstract
 */
export abstract class Modal {
/**
 * @param {?} overlay
 */
constructor(public overlay: Overlay) { }
/**
 * @return {?}
 */
alert(): ModalControllingContextBuilder<any> {
    throw new UnsupportedDropInError('alert');
  }
/**
 * @return {?}
 */
prompt(): ModalControllingContextBuilder<any> {
    throw new UnsupportedDropInError('prompt');
  }
/**
 * @return {?}
 */
confirm(): ModalControllingContextBuilder<any> {
    throw new UnsupportedDropInError('confirm');
  }
/**
 * Opens a modal window inside an existing component.
 * @param {?} content The content to display, either string, template ref or a component.
 * @param {?=} config Additional settings.
 * @return {?}
 */
open(content: ContainerContent, config?: OverlayConfig): Promise<DialogRef<any>> {
    config = config || /** @type {?} */(( {} as any));
    try {
      let /** @type {?} */ dialogs = this.overlay.open(config, this.constructor);

      if (dialogs.length > 1) {
        console.warn(`Attempt to open more then 1 overlay detected.
        Multiple modal copies are not supported at the moment, 
        only the first viewContainer will display.`);
      }
      // TODO:  Currently supporting 1 view container, hence working on dialogs[0].
      //        upgrade to multiple containers.
      return Promise.resolve(
        this.create(dialogs[0], content, config.bindings)
      );

    } catch ( /** @type {?} */e) {
      return Promise.reject<DialogRef<any>>(e);
    }

  }
/**
 * A Hook that enables derived classes to add content to the overlay.
 * @abstract
 * @param {?} dialogRef
 * @param {?} type
 * @param {?=} bindings
 * @return {?}
 */
create(dialogRef: DialogRef<any>,
                            type: ContainerContent,
                            bindings?: ResolvedReflectiveProvider[]) {}
/**
 * @template T
 * @param {?} dialogRef
 * @param {?} BackdropComponent
 * @return {?}
 */
protected createBackdrop<T>(dialogRef: DialogRef<any>, BackdropComponent: Class<T>): ComponentRef<T> {
    const /** @type {?} */ b = ReflectiveInjector.resolve([{provide: DialogRef, useValue: dialogRef}]);
    return dialogRef.overlayRef.instance.addComponent<T>(BackdropComponent, b);
  }
/**
 * @template T
 * @param {?} dialogRef
 * @param {?} ContainerComponent
 * @param {?} content
 * @param {?=} bindings
 * @return {?}
 */
protected createContainer<T>(
    dialogRef: DialogRef<any>,
    ContainerComponent: Class<T>,
    content: string | TemplateRef<any> | Type<any>,
    bindings?: ResolvedReflectiveProvider[]): ComponentRef<T> {

    const /** @type {?} */ b = ReflectiveInjector.resolve([{provide: DialogRef, useValue: dialogRef}])
      .concat(bindings || []);

    let /** @type {?} */ nodes: any[] = dialogRef.overlayRef.instance.getProjectables(content, b);
    return dialogRef.overlayRef.instance.addComponent<T>(ContainerComponent, b, nodes);
  }
/**
 * A helper function for derived classes to create backdrop & container
 * @deprecated use createBackdrop and createContainer instead
 * @template B, C
 * @param {?} dialogRef
 * @param {?} backdrop
 * @param {?} container 
 * 
 * @return {?}
 */
protected createModal<B, C>(dialogRef: DialogRef<any>, backdrop: Class<B>, container: Class<C>)
                                : { backdropRef: ComponentRef<B>, containerRef: ComponentRef<C> } {
    const /** @type {?} */ b = ReflectiveInjector.resolve([{provide: DialogRef, useValue: dialogRef}]);

    return {
      backdropRef: dialogRef.overlayRef.instance.addComponent<B>(backdrop, b),
      containerRef: dialogRef.overlayRef.instance.addComponent<C>(container, b)
    };
  }

}

function Modal_tsickle_Closure_declarations() {
/** @type {?} */
Modal.prototype.overlay;
}

